import type {
  CordIntrospectionEnvelope,
  CordIntrospectionErrorCode,
  CordIntrospectionIdbRequest,
} from "@cord/shared"
import { loadPendingVoiceBlobForSession } from "@/lib/voice-blob-idb"

type InspectThreadRuntimeEntry = (args: { sessionId: string }) => unknown

type ExecuteBrowserStorageIntrospectionArgs = {
  inspectThreadRuntimeEntry: InspectThreadRuntimeEntry
  request: CordIntrospectionIdbRequest
}

type SessionStorageValueInspection = {
  key: string
  parsedValue: unknown | null
  parseStatus: "missing" | "parsed" | "invalid_json"
  rawValue: string | null
  valueLength: number | null
}

/**
 * The worker route already clamps low-level store scans to 100 rows, but the
 * browser-side executor keeps the same fallback so direct callers cannot dump an
 * unbounded object store if request validation changes later.
 */
const BROWSER_STORAGE_RECORD_SCAN_FALLBACK_LIMIT = 100

class BrowserStorageIntrospectionError extends Error {
  code?: CordIntrospectionErrorCode
  meta?: Record<string, unknown>

  constructor(args: {
    code?: CordIntrospectionErrorCode
    error: string
    meta?: Record<string, unknown>
  }) {
    super(args.error)
    this.code = args.code
    this.meta = args.meta
  }
}

function buildSuccessEnvelope(args: {
  data: unknown
  meta?: Record<string, unknown>
}): CordIntrospectionEnvelope {
  return {
    ok: true,
    kind: "idb",
    data: args.data,
    meta: args.meta,
  }
}

function buildErrorEnvelope(args: {
  code?: CordIntrospectionErrorCode
  error: string
  meta?: Record<string, unknown>
}): CordIntrospectionEnvelope {
  return {
    ok: false,
    error: args.error,
    code: args.code,
    meta: args.meta,
  }
}

function getIndexedDbFactory(): IDBFactory {
  if (typeof window === "undefined" || typeof window.indexedDB === "undefined") {
    throw new BrowserStorageIntrospectionError({
      code: "unsupported",
      error: "IndexedDB is not available in this browser context",
    })
  }

  return window.indexedDB
}

function getSessionStorage(): Storage {
  if (typeof window === "undefined") {
    throw new BrowserStorageIntrospectionError({
      code: "unsupported",
      error: "sessionStorage is not available in this browser context",
    })
  }

  try {
    return window.sessionStorage
  } catch (error) {
    throw new BrowserStorageIntrospectionError({
      code: "unsupported",
      error: error instanceof Error ? error.message : "sessionStorage is unavailable",
    })
  }
}

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error("IndexedDB request failed"))
  })
}

function transactionToPromise(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error ?? new Error("IndexedDB transaction failed"))
    transaction.onabort = () => reject(transaction.error ?? new Error("IndexedDB transaction aborted"))
  })
}

async function listIndexedDatabases(): Promise<Array<{ name: string; version: number | null }>> {
  const indexedDb = getIndexedDbFactory()
  if (typeof indexedDb.databases !== "function") {
    throw new BrowserStorageIntrospectionError({
      code: "unsupported",
      error: "This browser does not support IndexedDB database enumeration",
    })
  }

  const databases = await indexedDb.databases()
  return databases
    .filter((database) => typeof database.name === "string" && database.name.trim().length > 0)
    .map((database) => ({
      name: database.name ?? "",
      version: typeof database.version === "number" && Number.isFinite(database.version)
        ? Math.floor(database.version)
        : null,
    }))
    .sort((left, right) => left.name.localeCompare(right.name))
}

async function openExistingDatabase(args: { dbName: string }): Promise<IDBDatabase> {
  const databases = await listIndexedDatabases()
  const matchingDatabase = databases.find((database) => database.name === args.dbName) ?? null
  if (!matchingDatabase) {
    throw new BrowserStorageIntrospectionError({
      code: "not_found",
      error: `IndexedDB database not found: ${args.dbName}`,
    })
  }

  const indexedDb = getIndexedDbFactory()
  return await new Promise((resolve, reject) => {
    const request = indexedDb.open(args.dbName)
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error(`Failed to open IndexedDB database: ${args.dbName}`))
  })
}

async function describeIndexedDatabase(args: { dbName: string }): Promise<unknown> {
  const db = await openExistingDatabase(args)
  try {
    const storeDescriptions = await Promise.all(
      Array.from(db.objectStoreNames).map(async (storeName) => {
        const transaction = db.transaction(storeName, "readonly")
        const store = transaction.objectStore(storeName)
        const indexes = Array.from(store.indexNames).map((indexName) => {
          const index = store.index(indexName)
          return {
            keyPath: index.keyPath ?? null,
            multiEntry: index.multiEntry,
            name: index.name,
            unique: index.unique,
          }
        })
        await transactionToPromise(transaction)
        return {
          autoIncrement: store.autoIncrement,
          indexes,
          keyPath: store.keyPath ?? null,
          name: store.name,
        }
      }),
    )

    return {
      dbName: db.name,
      stores: storeDescriptions,
      version: db.version,
    }
  } finally {
    db.close()
  }
}

function parseStructuredKey(args: { key: string }): IDBValidKey {
  const trimmedKey = args.key.trim()
  if (!trimmedKey) {
    throw new BrowserStorageIntrospectionError({
      code: "invalid_request",
      error: "key is required",
    })
  }

  const startsLikeJson =
    trimmedKey.startsWith("{") ||
    trimmedKey.startsWith("[") ||
    trimmedKey.startsWith('"') ||
    trimmedKey === "null" ||
    trimmedKey === "true" ||
    trimmedKey === "false" ||
    /^-?\d+(\.\d+)?$/.test(trimmedKey)

  if (!startsLikeJson) {
    return trimmedKey
  }

  try {
    return JSON.parse(trimmedKey) as IDBValidKey
  } catch {
    return trimmedKey
  }
}

async function countObjectStoreRecords(args: {
  dbName: string
  storeName: string
}): Promise<number> {
  const db = await openExistingDatabase({ dbName: args.dbName })
  try {
    const transaction = db.transaction(args.storeName, "readonly")
    const count = await requestToPromise(transaction.objectStore(args.storeName).count())
    await transactionToPromise(transaction)
    return count
  } catch (error) {
    throw new BrowserStorageIntrospectionError({
      code: "not_found",
      error: error instanceof Error ? error.message : "Failed to count object store records",
      meta: {
        dbName: args.dbName,
        storeName: args.storeName,
      },
    })
  } finally {
    db.close()
  }
}

async function getObjectStoreRecordByKey(args: {
  dbName: string
  key: string
  storeName: string
}): Promise<unknown> {
  const db = await openExistingDatabase({ dbName: args.dbName })
  try {
    const transaction = db.transaction(args.storeName, "readonly")
    const objectStore = transaction.objectStore(args.storeName)
    const primaryKey = parseStructuredKey({ key: args.key })
    const value = await requestToPromise(objectStore.get(primaryKey))
    await transactionToPromise(transaction)
    return {
      found: value !== undefined,
      primaryKey,
      value: value ?? null,
    }
  } catch (error) {
    throw new BrowserStorageIntrospectionError({
      code: "not_found",
      error: error instanceof Error ? error.message : "Failed to read object store record",
      meta: {
        dbName: args.dbName,
        storeName: args.storeName,
      },
    })
  } finally {
    db.close()
  }
}

async function listObjectStoreRecords(args: {
  dbName: string
  limit: number
  storeName: string
}): Promise<unknown> {
  const db = await openExistingDatabase({ dbName: args.dbName })
  try {
    const transaction = db.transaction(args.storeName, "readonly")
    const objectStore = transaction.objectStore(args.storeName)
    const valuesRequest = objectStore.getAll(undefined, args.limit)
    const keysRequest = objectStore.getAllKeys(undefined, args.limit)
    const [values, primaryKeys] = await Promise.all([
      requestToPromise(valuesRequest),
      requestToPromise(keysRequest),
    ])
    const totalCount = await requestToPromise(objectStore.count())
    await transactionToPromise(transaction)

    return {
      dbName: args.dbName,
      hasMore: totalCount > values.length,
      limit: args.limit,
      records: values.map((value, index) => ({
        primaryKey: primaryKeys[index] ?? null,
        value,
      })),
      returnedCount: values.length,
      storeName: args.storeName,
      totalCount,
    }
  } catch (error) {
    throw new BrowserStorageIntrospectionError({
      code: "not_found",
      error: error instanceof Error ? error.message : "Failed to list object store records",
      meta: {
        dbName: args.dbName,
        storeName: args.storeName,
      },
    })
  } finally {
    db.close()
  }
}

function parseJsonValue(args: { rawValue: string | null }): {
  parsedValue: unknown | null
  parseStatus: "missing" | "parsed" | "invalid_json"
} {
  if (args.rawValue === null) {
    return {
      parsedValue: null,
      parseStatus: "missing",
    }
  }

  try {
    return {
      parsedValue: JSON.parse(args.rawValue) as unknown,
      parseStatus: "parsed",
    }
  } catch {
    return {
      parsedValue: null,
      parseStatus: "invalid_json",
    }
  }
}

function inspectSessionStorageValue(args: { key: string }): SessionStorageValueInspection {
  const storage = getSessionStorage()
  const rawValue = storage.getItem(args.key)
  const parsed = parseJsonValue({ rawValue })
  return {
    key: args.key,
    parsedValue: parsed.parsedValue,
    parseStatus: parsed.parseStatus,
    rawValue,
    valueLength: rawValue?.length ?? null,
  }
}

function listSessionStorageKeys(): unknown {
  const storage = getSessionStorage()
  const entries: Array<{ key: string; valueLength: number | null }> = []
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index)
    if (!key) continue
    const value = storage.getItem(key)
    entries.push({
      key,
      valueLength: value?.length ?? null,
    })
  }

  entries.sort((left, right) => left.key.localeCompare(right.key))
  return {
    backend: "sessionStorage",
    entries,
  }
}

async function inspectPendingVoiceBlobMetadata(args: { sessionId: string }): Promise<unknown> {
  const entry = await loadPendingVoiceBlobForSession(args.sessionId)
  return {
    entry: entry
      ? {
          createdAt: entry.createdAt,
          hasR2Key: typeof entry.r2Key === "string" && entry.r2Key.length > 0,
          key: entry.key,
          mime: entry.mime,
          r2Key: entry.r2Key ?? null,
          sessionId: entry.sessionId,
          sizeBytes: entry.blob.size,
        }
      : null,
    sessionId: args.sessionId,
  }
}

export async function executeBrowserStorageIntrospectionRequest(
  args: ExecuteBrowserStorageIntrospectionArgs,
): Promise<CordIntrospectionEnvelope> {
  try {
    switch (args.request.op) {
      case "list_databases":
        return buildSuccessEnvelope({
          data: {
            databases: await listIndexedDatabases(),
          },
          meta: { backend: "indexedDB" },
        })
      case "list_session_storage_keys":
        return buildSuccessEnvelope({
          data: listSessionStorageKeys(),
          meta: { backend: "sessionStorage" },
        })
      case "describe_database":
        return buildSuccessEnvelope({
          data: await describeIndexedDatabase({ dbName: args.request.dbName }),
          meta: { backend: "indexedDB" },
        })
      case "get_session_storage_value":
        return buildSuccessEnvelope({
          data: inspectSessionStorageValue({ key: args.request.key }),
          meta: { backend: "sessionStorage" },
        })
      case "get_store_record_by_key":
        return buildSuccessEnvelope({
          data: await getObjectStoreRecordByKey({
            dbName: args.request.dbName,
            key: args.request.key,
            storeName: args.request.storeName,
          }),
          meta: { backend: "indexedDB" },
        })
      case "list_store_records":
        return buildSuccessEnvelope({
          data: await listObjectStoreRecords({
            dbName: args.request.dbName,
            limit: args.request.limit ?? BROWSER_STORAGE_RECORD_SCAN_FALLBACK_LIMIT,
            storeName: args.request.storeName,
          }),
          meta: { backend: "indexedDB" },
        })
      case "count_store_records":
        return buildSuccessEnvelope({
          data: {
            count: await countObjectStoreRecords({
              dbName: args.request.dbName,
              storeName: args.request.storeName,
            }),
            dbName: args.request.dbName,
            storeName: args.request.storeName,
          },
          meta: { backend: "indexedDB" },
        })
      case "get_session_draft":
        return buildSuccessEnvelope({
          data: await getObjectStoreRecordByKey({
            dbName: "cord-session-drafts",
            key: args.request.targetSessionId,
            storeName: "drafts",
          }),
          meta: {
            backend: "indexedDB",
            cache: "session_draft",
          },
        })
      case "get_thread_runtime_entry":
        return buildSuccessEnvelope({
          data: args.inspectThreadRuntimeEntry({ sessionId: args.request.targetSessionId }),
          meta: {
            backend: "memory",
            cache: "thread_sync_manager",
          },
        })
      case "get_pending_voice_blob_metadata":
        return buildSuccessEnvelope({
          data: await inspectPendingVoiceBlobMetadata({ sessionId: args.request.targetSessionId }),
          meta: {
            backend: "indexedDB",
            cache: "voice_blob",
          },
        })
      default:
        return buildErrorEnvelope({
          code: "unsupported",
          error: "Unsupported browser cache op",
        })
    }
  } catch (error) {
    if (error instanceof BrowserStorageIntrospectionError) {
      return buildErrorEnvelope({
        code: error.code,
        error: error.message,
        meta: error.meta,
      })
    }

    return buildErrorEnvelope({
      error: error instanceof Error ? error.message : "Browser cache introspection failed",
    })
  }
}
